import { useEffect, useState } from "react";
import { userRoles } from "@/lib/constants";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useUserNames } from "@/hooks/useUserNames";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

const EMPTY = {
  box_details: "", retailer_id: "", from_location: "", reason: "", company_id: "",
  retailer_doc_no: "", retailer_doc_date: "", retailer_doc_quantity: "",
  total_box_received: "", mode_of_transport: "", lr_no: "", retailer_received_by: "",
  received_by: "",
};

export default function BoxNotes() {
  const { user } = useAuth();
  const isAdmin = userRoles(user).includes("admin");
  const [rows, setRows] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const { nameFor } = useUserNames();

  const load = async () => {
    const [{ data: n }, { data: r }, { data: c }] = await Promise.all([
      client.get("/box-notes"), client.get("/retailers"), client.get("/companies"),
    ]);
    setRows(n); setRetailers(r); setCompanies(c);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const payload = {
        ...form,
        retailer_doc_quantity: Number(form.retailer_doc_quantity || 0),
        total_box_received: Number(form.total_box_received || 0),
      };
      await client.post("/box-notes", payload);
      toast.success("Saved"); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    try { await client.delete(`/box-notes/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const F = (label, key, type = "text") => (
    <div>
      <label className="label-cap">{label}</label>
      <Input data-testid={`boxnote-${key}-input`} type={type} value={form[key]} onChange={(e)=>setForm({...form, [key]: e.target.value})}/>
    </div>
  );

  return (
    <div>
      <PageHeader title="Box Notes" subtitle="Non-order boxes tracking"
        actions={<Button data-testid="boxnote-new-btn" onClick={()=>{setForm(EMPTY);setOpen(true);}} className="bg-slate-900 hover:bg-slate-800 rounded-sm"><Plus size={16} className="mr-1"/> New Note</Button>}
      />
      <Section>
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3">Box Details</th><th className="px-4 py-3">Retailer</th>
              <th className="px-4 py-3">Doc No</th><th className="px-4 py-3">Doc Date</th>
              <th className="px-4 py-3">Doc Qty</th><th className="px-4 py-3">Box Rec.</th>
              <th className="px-4 py-3">Mode</th><th className="px-4 py-3">LR No</th>
              <th className="px-4 py-3">Received By</th>
              <th className="px-4 py-3">Retailer Recd By</th><th className="px-4 py-3">From</th>
              <th className="px-4 py-3">Reason</th><th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Entered By</th><th className="px-4 py-3">At</th>
              {isAdmin && <th className="px-4 py-3">Actions</th>}
            </tr></thead>
            <tbody data-testid="boxnotes-tbody">
              {rows.map(n=>(
                <tr key={n.id} className="border-b border-slate-100 hover:bg-slate-50 align-top">
                  <td className="px-4 py-2">{n.box_details}</td>
                  <td className="px-4 py-2">{n.retailer_name}</td>
                  <td className="px-4 py-2 font-mono">{n.retailer_doc_no || "—"}</td>
                  <td className="px-4 py-2 font-mono">{n.retailer_doc_date || "—"}</td>
                  <td className="px-4 py-2 font-mono">{n.retailer_doc_quantity || "—"}</td>
                  <td className="px-4 py-2 font-mono">{n.total_box_received || "—"}</td>
                  <td className="px-4 py-2">{n.mode_of_transport || "—"}</td>
                  <td className="px-4 py-2 font-mono">{n.lr_no || "—"}</td>
                  <td className="px-4 py-2">{n.received_by || "—"}</td>
                  <td className="px-4 py-2">{n.retailer_received_by || "—"}</td>
                  <td className="px-4 py-2">{n.from_location}</td>
                  <td className="px-4 py-2 text-slate-700 max-w-xs">{n.reason}</td>
                  <td className="px-4 py-2">{n.company_name}</td>
                  <td className="px-4 py-2 text-xs">{nameFor(n.entered_by)}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">{new Date(n.created_at).toLocaleString()}</td>
                  {isAdmin && <td className="px-4 py-2"><button onClick={()=>remove(n.id)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-sm"><Trash size={14}/></button></td>}
                </tr>
              ))}
              {rows.length===0 && <tr><td colSpan={isAdmin?16:15} className="px-4 py-10 text-center text-slate-500">No box notes</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-3xl">
          <DialogHeader><DialogTitle>New Box Note</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-2">
            <div className="col-span-2">{F("Box Details", "box_details")}</div>
            <div>
              <label className="label-cap">Retailer</label>
              <Select value={form.retailer_id} onValueChange={(v)=>setForm({...form, retailer_id: v})}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent>{retailers.map(r=><SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-cap">Company</label>
              <Select value={form.company_id} onValueChange={(v)=>setForm({...form, company_id: v})}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent>{companies.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {F("Retailer Document No", "retailer_doc_no")}
            {F("Retailer Document Date", "retailer_doc_date", "date")}
            {F("Retailer Document Quantity", "retailer_doc_quantity", "number")}
            {F("Total Box Received", "total_box_received", "number")}
            {F("Mode of Transport", "mode_of_transport")}
            {F("LR No (Retailer Doc LR)", "lr_no")}
            {F("Received By", "received_by")}
            {F("Retailer Received By", "retailer_received_by")}
            {F("From", "from_location")}
            <div className="col-span-2"><label className="label-cap">Reason</label><Textarea value={form.reason} onChange={(e)=>setForm({...form, reason: e.target.value})} rows={3}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="boxnote-save-btn" onClick={create} className="bg-slate-900 hover:bg-slate-800 rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
